import { useTranslation } from "react-i18next";
import React from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";

import UnifiedLayout from "./UnifiedLayout";

// Import Layouts and Public Pages
import Homepage from "./Homepage";
import Login from "./Login";
import SignUp from "./SignUp";
import AcceptInvite from "./AcceptInvite";
import About from "./About";
import Support from "./Support";
import Compare from "./Compare";
import Activate from "./Activate";
import Privacy from "./Privacy";
import Contact from "./Contact";
import BetaPublicPage from "./BetaPublicPage";

// Import Protected App Components
import Dashboard from "./Dashboard";
import UserManagement from "./UserManagement";
import CustomLists from "./CustomLists";
import FindTest from "./FindTest";
import EditTest from "./EditTest";
import ViewTest from "./ViewTest";
import Profile from "./Profile";
import CorrectionsQueue from "./CorrectionsQueue";
import StalledActivations from "./StalledActivations";
import Backups from "./Backups";
import Provisioning from "./Provisioning";
import BetaPrivateHub from "./BetaPrivateHub";
import Reports from "./Reports";
import { ReportProvider } from "./reports/ReportContext";
import OrgUserReport from "./reports/OrgUserReport";
import PrintReport from "./reports/PrintReport";
import BasicTestDataReport from "./reports/BasicTestDataReport";
import "./styles/master.css";

const AdminRoute = ({ children }) => {
  const { currentUser, userRole } = useAuth();
  return currentUser &&
    (userRole === "admin" ||
      userRole === "superuser" ||
      userRole === "pro_user") ? (
    children
  ) : (
    <Navigate to="/app/dashboard" />
  );
};

const SuperuserRoute = ({ children }) => {
  const { currentUser, userRole } = useAuth();
  return currentUser && userRole === "superuser" ? (
    children
  ) : (
    <Navigate to="/app/dashboard" />
  );
};

const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  // If a user is logged in, show the child component. Otherwise, redirect.
  return currentUser ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

function AppRoutes() {
  const { currentUser, userRole, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return <div>{t("common.loadingApplication")}</div>;
  }

  // FREE users don't have web-app access (they get the warning at
  // /app/dashboard), so they shouldn't be auto-redirected away from the
  // public homepage. Logged-in PRO/admin/superuser users still get bounced
  // to their dashboard.
  const homepageElement =
    currentUser && userRole && userRole !== "free_user" ? (
      <Navigate to="/app/dashboard" />
    ) : (
      <Homepage />
    );

  return (
    <Routes>
      <Route element={<UnifiedLayout />}>
        {/* --- Public Routes --- */}
        <Route path="/" element={homepageElement} />
        <Route path="/beta" element={<BetaPublicPage />} />
        {/* Redirect only when the profile has actually resolved (userRole
            non-null). Otherwise an unactivated user who attempts to log
            in flips currentUser briefly, the route flips to <Navigate>,
            and Login.js unmounts before it can show the notActivated
            error. */}
        <Route
          path="/login"
          element={
            currentUser && userRole ? (
              <Navigate
                to={userRole === "free_user" ? "/app/beta" : "/app/dashboard"}
              />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/signup"
          element={currentUser ? <Navigate to="/app/dashboard" /> : <SignUp />}
        />
        {/* Always show AcceptInvite even if someone is already signed in
            (typically not the invitee). On submit, signInWithCustomToken
            replaces the current session with the new invitee's session. */}
        <Route path="/join/:token" element={<AcceptInvite />} />
        <Route path="/activate/:token" element={<Activate />} />
        <Route path="/about" element={<About />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />

        {/* --- Protected Application Routes --- */}
        {currentUser && (
          <>
            {/* --- General App Routes (Remain Unchanged) --- */}
            {/* Free users have no pro dashboard — send them to their landing
                (the Beta hub). This is the reusable free-user gating pattern:
                pro-only pages redirect free_user to /app/beta. */}
            <Route
              path="/app/dashboard"
              element={
                userRole === "free_user" ? (
                  <Navigate to="/app/beta" replace />
                ) : (
                  <Dashboard />
                )
              }
            />
            <Route
              path="/app/user-management"
              element={
                <AdminRoute>
                  <UserManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/app/corrections-queue"
              element={
                <AdminRoute>
                  <CorrectionsQueue />
                </AdminRoute>
              }
            />
            <Route
              path="/app/stalled-activations"
              element={
                <SuperuserRoute>
                  <StalledActivations />
                </SuperuserRoute>
              }
            />
            <Route
              path="/app/backups"
              element={
                <SuperuserRoute>
                  <Backups />
                </SuperuserRoute>
              }
            />
            <Route
              path="/app/provisioning"
              element={
                <SuperuserRoute>
                  <Provisioning />
                </SuperuserRoute>
              }
            />
            <Route
              path="/app/custom-lists"
              element={
                <AdminRoute>
                  <CustomLists />
                </AdminRoute>
              }
            />
            <Route path="/app/find-test" element={<FindTest />} />
            <Route path="/app/edit-test/:testId" element={<EditTest />} />
            <Route path="/app/view-test/:testId" element={<ViewTest />} />
            <Route path="/app/profile" element={<Profile />} />
            <Route path="/app/support" element={<Support />} />
            <Route path="/app/beta" element={<BetaPrivateHub />} />
            <Route path="/app/print-report/:testId" element={<PrintReport />} />

            {/* === NEW DEDICATED REPORTS SECTION === */}
            <Route
              path="/app/reports"
              element={
                <ReportProvider>
                  <Outlet />
                </ReportProvider>
              }
            >
              <Route index element={<Reports />} />
              <Route
                path="organization-user-report"
                element={
                  <SuperuserRoute>
                    <OrgUserReport />
                  </SuperuserRoute>
                }
              />
              <Route
                path="user-test-report"
                element={
                  <ProtectedRoute>
                    {" "}
                    {/* Use the new, more permissive guard */}
                    <BasicTestDataReport />
                  </ProtectedRoute>
                }
              />
              {/* This new route allows viewing a test *within* the report context */}
              <Route
                path="view-test/:testId"
                element={
                  <ProtectedRoute>
                    <ViewTest />
                  </ProtectedRoute>
                }
              />
            </Route>
          </>
        )}

        {/* This fallback is now correctly inside the layout */}
        <Route path="*" element={<Navigate to="/" />} />
      </Route>{" "}
      {/* 👈 This closes the UnifiedLayout Route */}
    </Routes> /* 👈 This closes the main Routes component */
  );
}

export default App;
